/**
 * Ciao AI Chat — API Layer
 * Communicates with the MP-AI chat server
 */

import { GetToken } from '../../../crud/common';

// In development, use localhost; in production, use the Azure Container Apps HTTPS URL
const CIAO_API_BASE = process.env.REACT_APP_CIAO_API_URL || 'https://mp-chat-api.icyfield-fe348118.eastus2.azurecontainerapps.io';

interface ChatSession {
  sessionId: string;
  localId: number;
  userId: number;
  createdAt: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  agentName?: string;
  toolsUsed?: string[];
}

/** Single confirmation item within a batch */
interface PendingConfirmationItem {
  toolName: string;
  actionDescription: string;
  summary: string;
  risk: 'low' | 'medium' | 'high';
  args: Record<string, unknown>;
}

/** Batch of pending write confirmations */
interface PendingConfirmation {
  id: string;
  items: PendingConfirmationItem[];
  overallRisk: 'low' | 'medium' | 'high';
}

interface SendMessageResponse {
  message: string;
  agent: string;
  metadata?: { toolCalls: number; routedTo: string };
  sessionId: string;
  pendingConfirmation?: PendingConfirmation;
}

/** Expert metadata from the backend registry */
export interface ExpertInfo {
  id: string;
  displayName: string;
  description: string;
  icon: string;
  capabilities: string[];
  quickActions: string[];
  greeting: string;
}

interface SessionSettings {
  autoExecuteWrites: boolean;
}

interface ChatHistory {
  sessionId: string;
  messages: ChatMessage[];
}

async function getHeaders(): Promise<Record<string, string>> {
  const token = GetToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Fetch available experts from the backend registry (cached per page load) */
let cachedExperts: ExpertInfo[] | null = null;
export async function getExperts(): Promise<ExpertInfo[]> {
  if (cachedExperts) return cachedExperts;
  try {
    const res = await fetch(`${CIAO_API_BASE}/api/chat/experts`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    cachedExperts = data.experts || [];
    return cachedExperts!;
  } catch (err) {
    console.warn('[Ciao] Failed to fetch experts, using defaults');
    return [{ id: 'auto', displayName: 'Auto', description: 'Enrutamiento automático', icon: '✨', capabilities: [], quickActions: [], greeting: '¡Hola! Soy Ciao, tu asistente.' }];
  }
}

export async function createChatSession(): Promise<ChatSession> {
  const headers = await getHeaders();
  console.log('[Ciao] Creating session, API base:', CIAO_API_BASE, 'has token:', !!headers.Authorization);
  let res: Response;
  try {
    res = await fetch(`${CIAO_API_BASE}/api/chat/sessions`, {
      method: 'POST',
      headers,
    });
  } catch (networkErr: any) {
    console.error('[Ciao] Network error:', networkErr);
    throw new Error(`No se pudo conectar: ${networkErr.message}`);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    console.error('[Ciao] Session creation failed:', res.status, err);
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  // Server returns "id", map to "sessionId"
  return { ...data, sessionId: data.sessionId || data.id };
}

export async function sendMessage(sessionId: string, message: string, files?: File[], expertId?: string): Promise<SendMessageResponse> {
  const token = GetToken();

  // If files are attached, use FormData (multipart); otherwise, use JSON
  if (files && files.length > 0) {
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('message', message);
    if (expertId) formData.append('expertId', expertId);
    files.forEach((f) => formData.append('files', f));

    // Build headers without Content-Type — browser sets multipart boundary automatically
    const multipartHeaders: Record<string, string> = {};
    if (token) multipartHeaders['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${CIAO_API_BASE}/api/chat/message`, {
      method: 'POST',
      headers: multipartHeaders,
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to send message' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // Text-only: keep existing JSON path for backward compatibility
  const headers = await getHeaders();
  const res = await fetch(`${CIAO_API_BASE}/api/chat/message`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ sessionId, message, ...(expertId ? { expertId } : {}) }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send message' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function getChatHistory(sessionId: string): Promise<ChatHistory> {
  const headers = await getHeaders();
  const res = await fetch(`${CIAO_API_BASE}/api/chat/sessions/${sessionId}`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to get history' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function listSessions(): Promise<ChatSession[]> {
  const headers = await getHeaders();
  const res = await fetch(`${CIAO_API_BASE}/api/chat/sessions`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.sessions || [];
}

/** Update session settings (e.g., autoExecuteWrites toggle) */
export async function updateSessionSettings(
  sessionId: string,
  settings: Partial<SessionSettings>,
): Promise<SessionSettings> {
  const headers = await getHeaders();
  const res = await fetch(`${CIAO_API_BASE}/api/chat/sessions/${sessionId}/settings`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(settings),
  });
  if (!res.ok) {
    throw new Error('Failed to update settings');
  }
  const data = await res.json();
  return data.settings;
}

/** Confirm or cancel a pending write action */
export async function confirmAction(
  sessionId: string,
  confirmationId: string,
  action: 'accept' | 'cancel',
): Promise<SendMessageResponse> {
  const headers = await getHeaders();
  const res = await fetch(`${CIAO_API_BASE}/api/chat/confirm`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ sessionId, confirmationId, action }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Confirmation failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export type { ChatSession, ChatMessage, SendMessageResponse, ChatHistory, PendingConfirmation, PendingConfirmationItem, SessionSettings };
